import {isAnyOf} from '@reduxjs/toolkit'

import {keyBy} from '@shared/helpers/collection'
import {formatISO} from '@shared/helpers/date'
import {isZero} from '@shared/helpers/lang'
import {mapValues} from '@shared/helpers/object'

import {
    getSaleHistoryAssignmentsByApi,
    getSaleHistoryAssignmentsDetailByApi,
    getSaleHistoryByApi,
    getSaleHistoryDetailByApi,
    getSaleHistorySearchByApi,
    SaleHistoryDetailResponse,
} from '@web/actions/saleHistory'
import {APIStatus} from '@web/core/constants'
import {ROUTER_ORDER_SALE_HISTORY} from '@web/core/routes'
import {
    BundleType,
    ContactType,
    DeliveryPaymentType,
    getProductBundleInitialState,
    NormalProduct,
    ProductType,
    ReceiverTransactionDateType,
    SubBundleType,
} from '@web/slices/productBundle/slice'
import makeSlice from '@web/slices/productBundle/sliceMaker'

import {initSaleHistory} from '../../actions/saleHistory'
import {initSaleHistoryDetail} from '../../actions/saleHistoryDetail'
import {
    AssignmentTypeMap,
    DeliveryPaymentTypeMap,
    SaleDetailProductStatusMap,
    SaleHistoryProductStatusMap,
} from '../../constants'

type PayloadType = ReturnType<typeof getSaleHistoryDetailByApi.fulfilled>['payload']

function getDeliveryPaymentType({order, productOrders}: Pick<PayloadType['detail'], 'order' | 'productOrders'>) {
    if (productOrders.some(({deliveryMethodType}) => deliveryMethodType === 'NOTHING')) {
        return DeliveryPaymentType.DIRECT
    }
    return DeliveryPaymentTypeMap[order.deliveryFeeRatingClassType ?? 'UNKNOWN']
}

const productBundleSlice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(
                // 판매상세 조회
                getSaleHistoryDetailByApi.fulfilled,
                (
                    state,
                    {
                        payload: {
                            detail: {order, productOrders, pay},
                        },
                    },
                ) => {
                    state.sequentialBundleKeys = [
                        {
                            key: order.orderNo,
                            subBundles: [
                                {key: order.orderNo, products: productOrders.map(({productOrderNo}) => productOrderNo)},
                            ],
                        },
                    ]

                    state.bundleMap = {
                        [order.orderNo]: {
                            type: BundleType.NORMAL,
                            id: order.maskedNaverId,
                            name: order.purchaserName,
                            labels: [],
                            contacts: order.fleaMarketTradTalkChannelId
                                ? [
                                      {
                                          type: ContactType.TALK_TALK,
                                          extra: {
                                              channelId: order.fleaMarketTradTalkChannelId,
                                          },
                                      },
                                  ]
                                : [],
                        },
                    }
                    state.subBundleMap = {
                        [order.orderNo]: {
                            bundleKey: order.orderNo,
                            type: SubBundleType.DELIVERY,
                            extra: {
                                delivery: {
                                    fee: order.deliveryFee ?? 0,
                                    paymentType: getDeliveryPaymentType({
                                        order,
                                        productOrders,
                                    }),
                                    isOversea: false,
                                },
                            },
                        },
                    }
                    state.productMap = mapValues(
                        (product): NormalProduct => ({
                            bundleKey: order.orderNo,
                            subBundleKey: order.orderNo,
                            type: ProductType.NORMAL,
                            status: {
                                type: SaleHistoryProductStatusMap[product.productOrderStatusType],
                                name: product.exposureStatusType,
                                description: product.exposureText,
                            },
                            id: product.productOrderNo,
                            name: {
                                text: product.productName,
                                href: product.productUrl,
                            },
                            image: {
                                src: product.productImageUrl,
                                href: product.productUrl,
                            },
                            price: {
                                original: pay.totalProductAmount,
                            },
                            quantity: product.initOrderQuantity,
                            naverMembership: false,
                            assignment: {
                                deliveryMethod:
                                    product.deliveryMethodType === 'DELIVERY_DESIGNATED' ||
                                    product.deliveryMethodType === 'CONVENIENCE_DELIVERY'
                                        ? product.deliveryMethodType
                                        : 'CANCEL_REJECT',
                            },
                        }),
                        keyBy<SaleHistoryDetailResponse['detail']['productOrders'][number]>('productOrderNo')(
                            productOrders,
                        ),
                    )
                    state.status.bundle.type = APIStatus.RESOLVED
                },
            )
            .addMatcher(
                isAnyOf(
                    // 판매내역 페이지네이션 조회 (목록 초기화)
                    getSaleHistoryByApi.fulfilled,
                    // 판매내역 검색/필터용 조회 (목록 추가)
                    getSaleHistorySearchByApi.fulfilled,
                ),
                (state, {payload}) => {
                    payload.items.forEach((item) => {
                        state.sequentialBundleKeys.push({
                            // 주문번호
                            key: item.orderNo,
                            subBundles: [
                                {
                                    // 판매내역 번호
                                    key: item.id,
                                    products: [
                                        // 상품주문번호
                                        item.productOrderNo,
                                    ],
                                },
                            ],
                        })

                        state.bundleMap[item.orderNo] = {
                            type: BundleType.NORMAL,
                            labels: [],
                            contacts: [],
                        }
                        state.subBundleMap[item.id] = {
                            bundleKey: item.orderNo,
                            type: SubBundleType.NORMAL,
                        }
                        state.productMap[item.productOrderNo] = {
                            bundleKey: item.orderNo,
                            subBundleKey: item.id,
                            type: ProductType.SELLING,
                            status: {
                                type: SaleDetailProductStatusMap[item.status.name],
                                name: item.status.text,
                                description: item.message,
                            },
                            id: item.productOrderNo,
                            name: {text: item.productName},
                            image: {src: item.productImageUrl, href: item.productUrl},
                            detailUrl: ROUTER_ORDER_SALE_HISTORY.DETAIL.getHref(item.orderNo),
                            price: {
                                original: item.productAmount,
                            },
                            naverMembership: false,
                            assignment: {
                                deliveryMethod:
                                    item.deliveryMethodPayTypeCode === 'DELIVERY_DESIGNATED' ||
                                    item.deliveryMethodPayTypeCode === 'CONVENIENCE_DELIVERY'
                                        ? item.deliveryMethodPayTypeCode
                                        : 'CANCEL_REJECT',
                            },
                            extra: {
                                receiver: {
                                    transactionDate: formatISO(item.time),
                                    dateType: ReceiverTransactionDateType.PAYMENT,
                                    name: item.orderMemberName,
                                },
                            },
                        }
                    })
                    state.status.bundle.type = APIStatus.RESOLVED
                },
            )
            .addMatcher(isAnyOf(getSaleHistoryAssignmentsDetailByApi.pending), (state) => {
                state.status.assignment.type = APIStatus.PENDING
                state.status.assignment.reloadCount -= 1
            })
            .addMatcher(
                isAnyOf(getSaleHistoryAssignmentsByApi.fulfilled, getSaleHistoryAssignmentsDetailByApi.fulfilled),
                (state, {payload}) => {
                    state.status.assignment.type = APIStatus.RESOLVED
                    payload.forEach(({productOrderNo, escrowSellerActions}) => {
                        state.assignmentMap[productOrderNo] = escrowSellerActions.map(
                            ({type, text, additionalInfo}) => ({
                                type: AssignmentTypeMap[type],
                                name: text,
                                // 추후 type 에 따라 split 처리 필요
                                value: additionalInfo,
                            }),
                        )
                    })
                },
            )
            .addMatcher(
                isAnyOf(getSaleHistoryAssignmentsByApi.rejected, getSaleHistoryAssignmentsDetailByApi.rejected),
                (state) => {
                    state.status.assignment.type = isZero(state.status.assignment.reloadCount)
                        ? APIStatus.DISABLED
                        : APIStatus.REJECTED
                },
            )
            .addMatcher(isAnyOf(initSaleHistory.pending, initSaleHistoryDetail.pending), () => {
                const state = getProductBundleInitialState()

                state.status.bundle.type = APIStatus.FETCHING

                return state
            })
            .addMatcher(isAnyOf(initSaleHistory.rejected, initSaleHistoryDetail.rejected), (state) => {
                state.status.bundle.type = APIStatus.REJECTED
            })
    },
})

export default productBundleSlice.reducer
